import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import EmptyData from "../../../StyledComponents/EmptyData";

import {
  Table,
  TableRow,
  TableHead,
  TableBody,
  // TableHeadCell,
  TableBodyCell,
} from "../../../StyledComponents";
import { Div } from "../../Styles";
import {
  TxtSpan,
  NewTableHeadCell,
  AvatarFrame,
  AvatarTxt,
  NewStatusBadge,
  NewActionButton,
} from "../styles";
import { GetSingleInvoiceData } from "../../../../StateManagement/Reducers/BillingState";
import InvoicePopup from "../InvoiceBalance/InvoicePopup";

const PaymentHistory = ({ PaymentLists }) => {
  const dispatch = useDispatch();

  const [invoiceModal, setInvoiceModal] = useState(false);
  const [invId, setInvId] = useState("");
  const [buttonDisable, setButtonDis] = useState(false);
  const CardDetails = useSelector((state) => state.Billing.CardDetail);
  const InvoList = useSelector((state) => state.Billing.InvoList);

  const DefaultCardDetails = CardDetails.map(
    (card) => card.def === "1" && card.id,
  );

  const getFullRow = (row) => {
    const normalize = (id) => {
      if (!id) return null;
      return String(id).replace(/[^0-9]/g, "");
    };

    // Stage 1: Collect ALL alphanumeric values from the row as potential IDs
    const rowVals = Object.entries(row);
    const rowPotentialIds = rowVals
      .map(([k, v]) => normalize(v))
      .filter((v) => v && v.length >= 3);
    const rowStrings = rowVals
      .map(([k, v]) => (typeof v === "string" ? v.toLowerCase().trim() : ""))
      .filter(
        (v) => v.length >= 3 && !v.includes("paid") && !v.includes("card"),
      );

    // Stage 2: Deep Cross-reference InvoList (Exhaustive Search)
    let invDetails = InvoList.find((inv) => {
      // Check numeric ID intersection
      const invIds = [
        normalize(inv.invoice_id),
        normalize(inv.invoice_no),
        normalize(inv.id),
        normalize(inv.invo_id),
        normalize(inv.InvoiceID),
      ].filter(Boolean);
      const hasIdMatch = rowPotentialIds.some((rid) => invIds.includes(rid));
      if (hasIdMatch) return true;

      // Check string intersection (Provider name, etc.)
      const invStrings = Object.values(inv)
        .map((v) => (typeof v === "string" ? v.toLowerCase().trim() : ""))
        .filter((v) => v.length >= 3);
      const hasStringMatch = rowStrings.some((rs) => invStrings.includes(rs));
      return hasStringMatch;
    });

    // Stage 3: Date + Amount Fallback
    if (!invDetails) {
      const rowDate =
        row.PaymentDate || row.invoice_date || row.date || row.created_at || "";
      const rowAmt = parseFloat(
        row.total_billed ||
          row.pay_total ||
          row.total_amount ||
          row.amount ||
          0,
      );

      invDetails = InvoList.find((inv) => {
        const invDate = inv.invoice_date || inv.date || inv.created_at || "";
        const invAmt = parseFloat(
          inv.total_billed || inv.total_amount || inv.amount || 0,
        );
        return (
          rowDate &&
          invDate &&
          rowDate.includes(invDate.substring(0, 10)) &&
          Math.abs(rowAmt - invAmt) < 0.1
        );
      });
    }

    // Merge: invDetails (Rich Metadata) must overwrite row hyphens
    return invDetails ? { ...row, ...invDetails } : row;
  };

  const ViewInvoice = (row) => {
    setButtonDis(true);
    setTimeout(() => {
      setButtonDis(false);
    }, 1000);
    const fullRow = getFullRow(row);
    const invoiceId =
      fullRow.invoice_id ||
      fullRow.invoice_no ||
      fullRow.Invoice ||
      fullRow.invo_id ||
      fullRow.receipt_id ||
      fullRow.id;
    dispatch(GetSingleInvoiceData(invoiceId));
    setInvId(fullRow);
    setInvoiceModal(true);
  };

  const normalizeProviderName = (name) => {
    if (!name || typeof name !== "string") return name;
    return name
      .replace(/\s*,\s*/g, " ")
      .replace(/\s{2,}/g, " ")
      .trim();
  };

  const getProviderName = (row) => {
    const fullRow = getFullRow(row);
    // 1. Known high-priority fields
    const priority = [
      "providerName",
      "provider_name",
      "Provider",
      "facilityname",
      "doctor_name",
      "facility_name",
      "username",
    ];
    for (let p of priority) {
      if (fullRow[p] && fullRow[p] !== "-" && fullRow[p].length > 1)
        return normalizeProviderName(fullRow[p]);
    }
    // 2. Deep keyword search in ALL fields
    for (let [k, v] of Object.entries(fullRow)) {
      const kl = k.toLowerCase();
      if (
        (kl.includes("name") ||
          kl.includes("prov") ||
          kl.includes("facil") ||
          kl.includes("user")) &&
        v &&
        v !== "-" &&
        v !== "P" &&
        typeof v === "string" &&
        v.length > 2
      ) {
        return normalizeProviderName(v);
      }
    }
    return "-";
  };

  const getAvatarTxt = (row) => {
    const fullRow = getFullRow(row);
    if (fullRow.invo_fname_split && fullRow.invo_fname_split !== "-")
      return fullRow.invo_fname_split;
    if (fullRow.prov_name_split && fullRow.prov_name_split !== "-")
      return fullRow.prov_name_split;
    const name = getProviderName(row);
    return name && name !== "-" ? name.trim().charAt(0).toUpperCase() : "P";
  };

  const getInvoiceId = (row) => {
    const fullRow = getFullRow(row);
    const idKeys = [
      "invoice_id",
      "invoice_no",
      "InvoiceID",
      "Invoice",
      "receipt_id",
      "invo_id",
      "id",
      "receipt_no",
    ];
    for (let k of idKeys) {
      if (fullRow[k] && fullRow[k] !== "-" && String(fullRow[k]).length >= 2)
        return fullRow[k];
    }
    // Fallback: If no ID found, check if a field contains a long numeric string
    for (let v of Object.values(row)) {
      const nv = String(v).replace(/[^0-9]/g, "");
      if (nv.length >= 9) return nv;
    }
    return "-";
  };

  const getStatus = (row) => {
    const fullRow = getFullRow(row);
    const balance = parseFloat(fullRow.total_balance || 0);
    const billed = parseFloat(
      fullRow.total_billed || fullRow.total_amount || 0,
    );
    if (balance === 0) return "Paid";
    if (balance < billed && billed > 0) return "Partially Paid";
    return "Unpaid";
  };

  const formatAmount = (value) => {
    const strVal = String(value);
    // If it's a 10-digit ID (like 2032664595), ignore it
    if (strVal.length >= 9 && !strVal.includes(".")) return "0.00";
    const num = parseFloat(value);
    if (isNaN(num) || num > 1000000) return "0.00";
    return num.toFixed(2);
  };

  return (
    <Div>
      <Table>
        <TableHead>
          <TableRow
            display="grid"
            tempColumns="18% 15% 12% 10% 10% 13% 10% 12%"
            background="#F1F7FF"
          >
            <NewTableHeadCell padding="16px 0px 16px 20px">
              Provider
            </NewTableHeadCell>
            <NewTableHeadCell padding="16px 0px 16px 20px">
              Paid On
            </NewTableHeadCell>
            <NewTableHeadCell padding="16px 0px 16px 20px">
              Invoice
            </NewTableHeadCell>
            <NewTableHeadCell padding="16px 0px 16px 20px">
              Billed
            </NewTableHeadCell>
            <NewTableHeadCell padding="16px 0px 16px 20px">
              Balance
            </NewTableHeadCell>
            <NewTableHeadCell padding="16px 0px 16px 20px">
              Payment Method
            </NewTableHeadCell>
            <NewTableHeadCell padding="16px 0px 16px 20px">
              Status
            </NewTableHeadCell>
            <NewTableHeadCell padding="16px 0px" textAlign="center">
              Action
            </NewTableHeadCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {PaymentLists.length !== 0 ? (
            <>
              {PaymentLists.map((row, ind) => (
                <TableRow
                  display="grid"
                  tempColumns="18% 15% 12% 10% 10% 13% 10% 12%"
                  key={ind}
                  alignItems="center"
                  id={`bil_pay-row-${ind}`}
                >
                  <TableBodyCell padding="16px 0px 16px 20px">
                    <Div display="flex" alignItems="center">
                      <AvatarFrame>
                        <AvatarTxt>{getAvatarTxt(row)}</AvatarTxt>
                      </AvatarFrame>
                      <TxtSpan fontWeight="500" color="#1e293b">
                        {getProviderName(row)}
                      </TxtSpan>
                    </Div>
                  </TableBodyCell>

                  <TableBodyCell padding="16px 0px 16px 20px">
                    <TxtSpan color="#64748b">
                      {(() => {
                        const rawDate = row.invoice_date || row.PaymentDate;
                        const formatted =
                          rawDate && rawDate.includes(",")
                            ? new Date(rawDate).toISOString().split("T")[0]
                            : rawDate;
                        return formatted || "-";
                      })()}
                    </TxtSpan>
                  </TableBodyCell>

                  <TableBodyCell padding="16px 0px 16px 20px">
                    <TxtSpan color="#2b59ff" fontWeight="500">
                      {getInvoiceId(row)}
                    </TxtSpan>
                  </TableBodyCell>

                  <TableBodyCell padding="16px 0px 16px 20px">
                    <TxtSpan fontWeight="600" color="#1e293b">
                      $
                      {formatAmount(
                        row.total_billed ||
                          row.pay_total ||
                          row.total_amount ||
                          row.amount ||
                          0,
                      )}
                    </TxtSpan>
                  </TableBodyCell>

                  <TableBodyCell padding="16px 0px 16px 20px">
                    <TxtSpan fontWeight="600" color="#1e293b">
                      $
                      {parseFloat(
                        row.total_balance || row.balance || 0,
                      ).toFixed(2)}
                    </TxtSpan>
                  </TableBodyCell>

                  <TableBodyCell padding="16px 0px 16px 20px">
                    <TxtSpan color="#475569">
                      {row.paymentMethod ||
                        row.payment_method ||
                        row.pay_method ||
                        "Credit Card"}
                    </TxtSpan>
                  </TableBodyCell>

                  <TableBodyCell padding="16px 0px 16px 20px">
                    <NewStatusBadge status={getStatus(row)}>
                      {getStatus(row)}
                    </NewStatusBadge>
                  </TableBodyCell>

                  <TableBodyCell padding="16px 0px">
                    <Div display="flex" justifyContent="center">
                      <NewActionButton
                        variant="outline"
                        onClick={() => ViewInvoice(row)}
                        disabled={buttonDisable}
                      >
                        View
                      </NewActionButton>
                    </Div>
                  </TableBodyCell>
                </TableRow>
              ))}
            </>
          ) : (
            <TableRow>
              <TableBodyCell colSpan={8} textAlign="center">
                <EmptyData />
              </TableBodyCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      {invoiceModal && (
        <InvoicePopup
          show={invoiceModal}
          Data={invId}
          cardDetails={CardDetails}
          defaultcardcheck={DefaultCardDetails[0]}
          onHide={() => setInvoiceModal(false)}
        />
      )}
    </Div>
  );
};

export default PaymentHistory;
